import React, { useEffect, useState } from 'react'
import {
  Legend,
  ReportHeading,
  SocialLegend,
  LoaderWrapper,
  ReportOuterWrapper,
} from '../../../../components'
import ReportsService from '../../../../services/reports.service'
import * as d3 from 'd3'
import { SOCIAL_IMAGE_MAP } from '../../../../constants'
import { useUpdateLhs, useReactQuery, useWindowResize } from '../../../../hooks'
import { getObjectAsArray } from '../../../../util'

import InfoRow from './infoRow'
import {
  OuterWrapper,
  InnerWrapper,
  ContentWrapper,
  ImpactDestinationsWrapper,
  LegendWrapper,
} from './style'

var margin = { top: 10, right: 0, bottom: 20, left: 0 },
  height = 500

const drawScatter = (data, props, setExlData) => {
  const wrapper = document.getElementById(props.type)

  const chartContainer = wrapper?.querySelector('#scatter-chart')
  const totalWidth = chartContainer ? chartContainer.clientWidth : 600

  // 1. Target the SVG element first
  const svgSelection = d3
    .select(chartContainer)
    .select('svg')
    .attr('width', totalWidth)
    .attr('height', height + 30)
    .attr('viewBox', `0 0 ${totalWidth} ${height + 30}`)
    .attr('preserveAspectRatio', 'none')
    .style('display', 'block')
    .html('')

  // 2. Fetch runtime layout metrics directly from the bound DOM node
  const svgNode = svgSelection.node()
  const svgBoundWidth = svgNode ? svgNode.getBoundingClientRect().width : totalWidth
  const svgBoundHeight = svgNode ? svgNode.getBoundingClientRect().height : (height + 30)

  // Append internal coordinate translation group
  const svg = svgSelection
    .append('g')
    .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')

  const x = d3
    .scaleLog()
    .domain([
      d3.min(data, (d) => parseInt(d.Engagement)),
      d3.max(data, (d) => parseInt(d.Engagement)),
    ])
    .range([0, svgBoundWidth * 0.9])

  const y = d3
    .scaleLog()
    .domain([d3.min(data, (d) => parseInt(d.Reach)), d3.max(data, (d) => parseInt(d.Reach))])
    .range([height, 0])

  // 4. Calculate center points relative to the full SVG canvas
  const visualMidX = svgBoundWidth / 2
  const visualMidY = svgBoundHeight / 2

  const defaultObj = {
    ConsumerCommentsAnalysis: "",
    CreatedDate: "",
    Engagement: 0,
    ExpertPostSentiment: "",
    ExpertPostTitle: "",
    ExpertPostURL: "",
    InsightsElementId: "",
    ModifiedDate: "",
    Name: "",
    NetSentiment: 0,
    Platform: "",
    Reach: 0,
    Standout: false,
    URL: ""
  }

  const topRight = [
    {...defaultObj, Name: "Quadrant 1 (High Engagement and High Reach)", Platform: "(Top Impact Destinations)"},
    {...defaultObj, Name: "Quadrant 1", Platform: "(Targets for Advertising)"},
    {...defaultObj, Name: "Quadrant 1", Platform: "(Targets for PR)"}
  ]
  const topLeft = [defaultObj, {...defaultObj, Name: "Quadrant 2 (Low Engagement and High Reach)", Platform: "(Targets for Advertising)"}]
  const bottomLeft = [defaultObj, {...defaultObj, Name: "Quadrant 3 (Low Engagement and Low Reach)", Platform: ""}]
  const bottomRight = [defaultObj, {...defaultObj, Name: "Quadrant 4 (High Engagement and Low Reach)", Platform: "(Targets for PR)"}]

  data.forEach((item) => {
    const pixelX = x(parseInt(item.Engagement))
    const pixelY = y(parseInt(item.Reach))

    if (pixelX < visualMidX && pixelY < visualMidY) {
      topLeft.push(item)
    } else if (pixelX >= visualMidX && pixelY < visualMidY) {
      topRight.push(item)
    } else if (pixelX < visualMidX && pixelY >= visualMidY) {
      bottomLeft.push(item)
    } else {
      bottomRight.push(item)
    }
  })

  if (setExlData) {
    // setExlData([...topLeft, ...topRight, ...bottomLeft, ...bottomRight])
    setExlData([...topRight, ...topLeft,  ...bottomLeft, ...bottomRight])
  }

  // --- DRAW CENTER LINES MATCHING ABSOLUTE CANVAS DIMENSIONS ---
  // Vertical axis line span
  svgSelection.append('line')
    .attr('x1', visualMidX)
    .attr('y1', 0)
    .attr('x2', visualMidX)
    .attr('y2', svgBoundHeight)
    .attr('stroke', '#7E7E7E')
    .attr('stroke-width', '1px')

  // Horizontal axis line span
  svgSelection.append('line')
    .attr('x1', 0)
    .attr('y1', visualMidY)
    .attr('x2', svgBoundWidth)
    .attr('y2', visualMidY)
    .attr('stroke', '#7E7E7E')
    .attr('stroke-width', '1px')
  // -------------------------------------------------------------

  const div = d3.select(chartContainer).select('div.tooltip')
  const group = svg.append('g')

  group
    .selectAll('dot')
    .data(data)
    .enter()
    .append('svg:image')
    .attr('xlink:href', (d) => {
      return SOCIAL_IMAGE_MAP[`${d.Platform.toLowerCase().replaceAll(' ','')}${d?.Standout ? '_standout' : ''}`]
    })
    .attr('x', function (d) {
      return x(parseInt(d.Engagement))
    })
    .attr('y', function (d) {
      return y(parseInt(d.Reach)) - 12
    })
    .attr('height', '15px')
    .style('cursor', 'pointer')
    .attr('id', function (d) {
      return `id_${x(parseInt(d.Engagement))}_${y(parseInt(d.Reach))}`
    })
    .on('click', onClick)

  group
    .selectAll('text')
    .data(data)
    .enter()
    .append('text')
    .text(
      (d) =>
        `${d.Name} ${d.NetSentiment ? `(${d.NetSentiment > 0 ? '+' + d.NetSentiment : d.NetSentiment}%)` : ''}`
    )
    .attr('x', function (d) {
      return x(parseInt(d.Engagement))
    })
    .attr('y', function (d) {
      return y(parseInt(d.Reach)) + 15
    })
    .attr('font-size', '12px')
    .attr('fill', (d) =>
      parseInt(d.NetSentiment) < 0 ? 'red' : parseInt(d.NetSentiment) === 0 ? 'black' : 'green'
    )
    .style('cursor', 'pointer')
    .on('click', onClick)

  function onClick(events, d) {
    if (d?.ConsumerCommentsAnalysis?.length) {
      const position = {
        x: events.clientX - (window.scrollX + wrapper.getBoundingClientRect().left),
        y: events.clientY - (window.scrollY + wrapper.getBoundingClientRect().top),
      }
      if (position.x + 380 > wrapper.offsetWidth) {
        position.x = position.x - (position.x + 380 - wrapper.offsetWidth)
      }
      if (position.y + 450 > wrapper.offsetHeight) {
        position.y = position.y - (position.y + 450 - wrapper.offsetHeight)
      }

      const percent = d.NetSentiment > 0 ? 'positive' : 'negative'
      const addImage = d.Standout ? `<img src='${SOCIAL_IMAGE_MAP.standout}' alt='' />` : ''

      const content = `
      <div class='overlay' id='popupClose'></div>
        <div class='popup' style='top:${position.y}px; left:${position.x}px;'>
          <a class='close' href='javascript:' id='close'>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L9 9" stroke="#C3C3C3" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M9 1L1 9" stroke="#C3C3C3" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </a>
          <span>
            <img src='${SOCIAL_IMAGE_MAP[d.Platform.toLowerCase().replaceAll(' ','')]}' alt='${SOCIAL_IMAGE_MAP[d.Platform.toLowerCase().replaceAll(' ','')]}' />
            ${addImage}
          </span>
          <a class='link' target='_blank' href='${d?.URL}'>${d.Name}</a>
          <p class='percentage ${percent}'>${d.NetSentiment}%</p>
          <p class='description'>${d?.ConsumerCommentsAnalysis}</p>
          <p class='post'>Top Post</p>
          <p class='type ${d.ExpertPostSentiment}'><a class='link' target='_blank' href='${d?.ExpertPostURL || '#'}'>${d?.ExpertPostTitle}</a></p>
        </div>
      `
      div.html(content).style('display', 'block')
      div.select('a#close').on('click', function () {
        div.html('')
      })
      div.select('#popupClose').on('click', function () {
        div.html('')
      })
    } else {
      window.open(`${d.URL}`, '_blank')
    }
  }
}

const ReportImpactDestination = (props) => {
  const [loading, setLoading] = useState(false)
  const [socialLegend, setSocialLegend] = useState([])
  const [state, setState] = useState([])
  const [exlData, setExlData] = useState([[], [], [], []])
  const [legends, setLegends] = useState([
    { label: 'Positive', value: 'Positive' },
    { label: 'Neutral', value: 'Neutral' },
    { label: 'Mixed', value: 'Mixed' },
    { label: 'Negative', value: 'Negative' },
  ])
  const { updateSubLhsApiLoaded = () => {} } = useUpdateLhs() || {}
  const [eventCount, setEventCount] = useState(1)

  const useReactQueryObject = {
    payloadDetails: {
      queryId: [props.InsightsElementType, props.InsightsElementId],
      urlFunc: ReportsService.getImpactDestinationElement,
      payload: { insightsElementId: props.InsightsElementId },
    },
  }
  const { data, isLoading, isSuccess, isError, error, refetch } = useReactQuery(useReactQueryObject)

  useEffect(() => {
    if (!loading && state.length) {
      drawScatter(state, props, setExlData)
      setEventCount((p) => p + 1)
    }
  }, [loading, state])

  const onSuccessFunc = (resp) => {
    setState(resp)
    const objLegends = {}
    resp.forEach((element) => {
      objLegends[element.Platform] = ''
    })
    setSocialLegend(Object.keys(objLegends))
    updateSubLhsApiLoaded(props)

    const standoutArray = resp.map((d) => d.Standout).filter((f) => f === true)
    if (standoutArray.length > 0) {
      setLegends([...legends, { label: 'Standout Impact Destination', value: 'Standout' }])
    }
    setEventCount((p) => p + 1)
  }

  useEffect(() => {
    if (isSuccess && data?.length) onSuccessFunc(data)
  }, [isSuccess, data])

  const [progressionDropdownData, setProgressionDropdownData] = useState([])
  const [selectedObject, setSelectedObject] = useState()
  
  const loadReportProlgressionDropdown = (getLatest = false) => {
    if(props.InsightsElementType !== 'ImpactDestinationsAndProgression') return

    ReportsService.getImpactDestinationProgressionElement(
      { insightsElementId: props.InsightsElementId },
      getLatest
    )
    .then((resp) => {
        const respNew = getObjectAsArray(resp).sort((a, b) => a.PeriodOrder - b.PeriodOrder).map((item) => ({ ...item, label: item.PeriodName, value: item.PeriodName, selected:false }))
        respNew[0].selected = true
        setSelectedObject(respNew.find((item) => item.value === selectedObject) || respNew[0]?.PeriodName || '') 
        setProgressionDropdownData(respNew)
        
        setState(respNew[0]?.PeriodImpactDestinationElement?.ImpactDestinationElement)
      })
      .catch((error) => {
        const resMessage = error
        console.log('ERROR : ', resMessage)
      })
  }
  
  const progressionDropdownChangeFunc = (obj) => {
    setSelectedObject(obj.selectedObj)
    const stateArray = (obj?.selectedObj?.PeriodImpactDestinationElement?.ImpactDestinationElement || [])
    setState(stateArray)
  }
  
  useEffect(() => {
    loadReportProlgressionDropdown()
  }, [])

  useWindowResize(() => {
    if (!loading && state.length) {
      drawScatter(state, props, setExlData)
      setEventCount((p) => p + 1)
      setTimeout(() => {
        drawScatter(state, props, setExlData)
        setEventCount((p) => p + 1)
      }, 2500)
    }
  })


  return (
    <>
      {exlData?.length <= 5 ? null : (
        <ReportHeading
          resetInsightHeight={eventCount}
          downloadId={props.type}
          downloadIdData={exlData}
          progressionDropdownData={progressionDropdownData}
          progressionDropdownEvents={progressionDropdownChangeFunc}
          {...props}
        />
      )}
      <ReportOuterWrapper>
        <OuterWrapper id={props.type}>
          <LoaderWrapper
            loadOnlyApi={props.loadOnlyApi}
            isLoading={isLoading}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
          >
            <InnerWrapper>
              <ContentWrapper>
                <ImpactDestinationsWrapper
                  sx={{ marginLeft: '25px', marginBottom: '50px', minHeight: 500 }}
                  id='scatter-chart'
                >
                  <div className='tooltip'></div>
                  <div className='columnLine'>
                    <span>Reach</span>
                  </div>
                  <div className='rowLine'>
                    <span>Engagement</span>
                  </div>
                  <svg />
                </ImpactDestinationsWrapper>
                <InfoRow />
                <LegendWrapper className='legendWrapper'>
                  <Legend legends={legends} />
                  <SocialLegend legends={socialLegend} />
                </LegendWrapper>
              </ContentWrapper>
            </InnerWrapper>
          </LoaderWrapper>
        </OuterWrapper>
      </ReportOuterWrapper>
    </>
  )
}

export default ReportImpactDestination
